import axios, { AxiosError, AxiosHeaders } from "axios";
import {
  defaultRequestInterceptors,
  defaultResponseInterceptors,
  handleAuthExpired,
  isStaleSessionResponse,
} from "./config";

import {
  AxiosInstance,
  InternalAxiosRequestConfig,
  RequestConfig,
  AxiosResponse,
} from "./types";
import { ElMessage } from "element-plus";
import { REQUEST_TIMEOUT } from "@/constants";

export const PATH_URL =
  import.meta.env.VITE_API_BASE_PATH || "https://admin.test.triladtech.com/api";

// 图片上传接口前缀
export const IMG_PATH_URL =
  import.meta.env.VITE_IMG_API_BASE_PATH || PATH_URL;

const abortControllerMap: Map<string, AbortController> = new Map();

// This request must survive local logout and must not expire a subsequent login.
export const revokeSession = () => {
  try {
    const config = defaultRequestInterceptors({
      url: "/login/logout",
      method: "post",
      baseURL: PATH_URL,
      timeout: 5000,
      data: {},
      headers: new AxiosHeaders({ "Content-Type": "application/json" }),
    });
    void axios.request(config).catch((error) => {
      console.warn("[IdleSession] Server logout failed; local logout completed", error.message);
    });
  } catch (error) {
    console.warn("[IdleSession] Unable to send server logout", error);
  }
};

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL,
});

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController();
  const url = res.url || "";
  res.signal = controller.signal;
  abortControllerMap.set(url, controller);
  return res;
});

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || "";
    abortControllerMap.delete(url);
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res;
  },
  (error: AxiosError) => {
    // Error occurred
    if (!axios.isCancel(error) && !isStaleSessionResponse(error.config)) {
      if (error.response?.status === 401) handleAuthExpired(error.config);
      else ElMessage.error(error.message);
    }
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.request.use(defaultRequestInterceptors);
axiosInstance.interceptors.response.use(defaultResponseInterceptors);

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any);
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err: any) => {
          reject(err);
        });
    });
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url];
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort();
      abortControllerMap.delete(_url);
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort();
    }
    abortControllerMap.clear();
  },
};

export default service;
